import type { WorkstreamStatus } from "@/types";
import { Pill } from "@/components/ui";
import { deadlineChip, jobStageToPill, workstreamStatusLabel, workstreamStatusTone } from "@/lib/jobs";

/** Single pill for a job's derived overall status (`stage` on a list row,
 *  `status` on the detail job) — the 7-value job-level set, distinct from the
 *  per-type workstream rollups below. */
export function JobStagePill({ stage }: { stage: string }) {
  const pill = jobStageToPill(stage);
  return <Pill tone={pill.tone}>{pill.label}</Pill>;
}

/** Deadline urgency chip driven by the server's `days_remaining` (overdue / soon
 *  / future / none). Tones map to dedicated CSS classes — the "soon" amber uses
 *  the --warning token (a genuine due-soon warning, distinct from the quote
 *  status system's deliberate avoidance of it). */
export function DeadlineChipView({ daysRemaining }: { daysRemaining: number | null | undefined }) {
  const chip = deadlineChip(daysRemaining);
  return <span className={`job-chip job-chip--${chip.tone}`}>{chip.label}</span>;
}

/** The rollup fields mirrored onto JobListRow and JobDetail's job object
 *  (Addendum 2) — the shape WorkstreamPills reads. */
export interface WorkstreamFields {
  planning_status: WorkstreamStatus;
  bc_status: WorkstreamStatus;
  other_status: WorkstreamStatus;
  is_complete: boolean;
}

/** Derived per-type rollup pills (Addendum 2), one row per type that has drawing
 *  work (status ≠ not_applicable), tagged by type. Renders a single "Complete"
 *  pill when the job's current set is complete. Used in the job list cell + the
 *  detail header; the CALLER decides whether to show this or the legacy stage
 *  pill (see {@link hasDerivedWorkstreamStatus}). `compact` uses single-letter
 *  tags (P / B / D) for the dense list cell; the header keeps P / BC / Drawings. */
export function WorkstreamPills({ job, compact = false }: { job: WorkstreamFields; compact?: boolean }) {
  if (job.is_complete) {
    return (
      <span className="ws-item">
        <Pill tone="success">Complete</Pill>
      </span>
    );
  }
  const rows: { tag: string; status: WorkstreamStatus }[] = [];
  if (job.planning_status !== "not_applicable") rows.push({ tag: "P", status: job.planning_status });
  if (job.bc_status !== "not_applicable") rows.push({ tag: compact ? "B" : "BC", status: job.bc_status });
  if (job.other_status !== "not_applicable") rows.push({ tag: compact ? "D" : "Drawings", status: job.other_status });

  if (rows.length === 0) return <span className="job-drawer__muted">—</span>;
  return (
    <>
      {rows.map((r) => (
        <span key={r.tag} className="ws-item">
          <span className="ws-item__tag">{r.tag}</span>
          <Pill tone={workstreamStatusTone(r.status)}>{workstreamStatusLabel(r.status)}</Pill>
        </span>
      ))}
    </>
  );
}

/** One per-type workstream status, for the job list's split Planning / BC /
 *  Drawings columns (replaces the stacked {@link WorkstreamPills} cell there).
 *  `not_applicable` — that type has no drawings in the set — renders a muted "—". */
export function WorkstreamStatusCell({ status }: { status: WorkstreamStatus }) {
  if (status === "not_applicable") return <span className="jobs-table__na">—</span>;
  return <Pill tone={workstreamStatusTone(status)}>{workstreamStatusLabel(status)}</Pill>;
}
